import * as z from 'zod'

import raw from './taxSocialSecurityLimits.json'

// Social Security wage base limits + payroll tax rates by year. `limit`
// is the annual maximum wage subject to SS tax; `taxRate` is the
// employee share (SE variants combine differently). Statutory values
// published annually by SSA.
//
// Filing status is not a dimension here — SSA sets one number per year
// applied per-worker regardless of household filing status.
// `SocialSecurityTax.findLimitForYear(year)` takes no filingStatus arg.

export const taxSocialSecurityLimitSchema = z
  .object({
    year: z.int(),
    limit: z.number().positive(),
    taxRate: z.number().min(0).max(1),
  })
  .strict()

export type TaxSocialSecurityLimit = z.infer<
  typeof taxSocialSecurityLimitSchema
>

// Sorted ascending by year at parse time so consumers can rely on order.
// The calculator uses the tail (`.at(-1)`) as its forward-projection base;
// keeping the invariant here means the class needs no reduce/scan of its
// own. Guarded by an invariant test in the dictionary's __tests__.
export const TAX_SOCIAL_SECURITY_LIMITS: readonly TaxSocialSecurityLimit[] = z
  .array(taxSocialSecurityLimitSchema)
  .parse(raw)
  .sort((a, b) => a.year - b.year)

export const SOCIAL_SECURITY_LIMIT_BY_YEAR: ReadonlyMap<
  number,
  TaxSocialSecurityLimit
> = new Map(TAX_SOCIAL_SECURITY_LIMITS.map(l => [l.year, l]))

// Tail of the sorted array = latest published year. Consumers (e.g.
// SocialSecurityTax) use this as the base row for forward projection when
// the requested year has no explicit entry.
export const LATEST_SOCIAL_SECURITY_LIMIT: TaxSocialSecurityLimit =
  TAX_SOCIAL_SECURITY_LIMITS.at(-1)!
